import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { StatusCode } from '../../../components/Alert.tsx';
import FormRange from '../../../components/form/FormRange.tsx';
import { Form, FormActions, FormButton, FormInput, FormSelect } from '../../../components/form/index.js';
import { useAlert } from '../../../contexts/alertProvider.tsx';
import { useUsersContext } from '../../../contexts/users.tsx';
import { UserGoals } from '../../../models/users/userGoals.ts';

const goalTypeOptions = [
    { name: 'Loose weight -', goalType: 0, gramsOfProteinPerKg: 2, gramsOfFatPerKg: 1, descriptionAppend: 'If the body fat percentage is over 20% for men and 30% for women, the calculated protein intake is 40% of the total calorie count. Recommended if you exercise at least three times a week.' },
    { name: 'Maintain weight', goalType: 1, gramsOfProteinPerKg: 1.76, gramsOfFatPerKg: 1 },
    { name: 'Gain weight +', goalType: 2, gramsOfProteinPerKg: 2.2, gramsOfFatPerKg: 1 }
]

const distributionTypeOptions = [
    { name: 'Active', description: '{gramsOfProteinPerKg}g/kg protein, {gramsOfFatPerKg}g/kg fat, the rest carbs.', dailyProteinTarget: 2, dailyCarbohydratesTarget: 0, dailyTotalFatTarget: 1, distributionType: 0 },
    { name: 'Equilibrated', description: '20% protein, 50% carbs, 30% fat.', dailyProteinTarget: 20, dailyCarbohydratesTarget: 50, dailyTotalFatTarget: 30, distributionType: 1 },
    { name: 'Keto', description: '20% protein, 10% carbs, 70% fat.', dailyProteinTarget: 20, dailyCarbohydratesTarget: 10, dailyTotalFatTarget: 70, distributionType: 2 }
]

const validationSchema = Yup.object().shape({
    dailyCalorieTarget: Yup
        .number()
        .required('Daily Calorie Target is required')
        .typeError('Daily Calorie Target must be a number')
        .min(0, "Daily Calorie Target must be a positive number"),
    dailyProteinTarget: Yup
        .number()
        .required('Daily Protein Target is required')
        .typeError('Daily Protein Target must be a number')
        .min(0, "Daily Protein Target must be a positive number")
        .max(100, "Daily Protein Target must be less than 100"),
    dailyCarbohydratesTarget: Yup
        .number()
        .required('Daily Carbohydrates Target is required')
        .typeError('Daily Carbohydrates Target must be a number')
        .min(0, "Daily Carbohydrates Target must be a positive number")
        .max(100, "Daily Carbohydrates Target must be less than 100"),
    dailyTotalFatTarget: Yup
        .number()
        .required('Daily Total Fat Target is required')
        .typeError('Daily Total Fat Target must be a number')
        .min(0, "Daily Total Fat Target must be a positive number")
        .max(100, "Daily Total Fat Target must be less than 100")
});

const GoalsBody = () => {
    const { profile, fetchProfile, insertUserGoals } = useUsersContext();
    const [localUserGoals, setLocalUserGoals] = useState<UserGoals>({} as UserGoals);
    const [showCalculate, setShowCalculate] = useState<boolean>(false);
    const [calculationEntity, setCalculationEntity] = useState<any>({ percentageStep: 20 });
    const [calculatedBodyMeasurements, setCalculatedBodyMeasurements] = useState<any>({});
    const { showAlert } = useAlert();
    useEffect(() => {
        if (!profile || profile == null)
            fetchProfile();
        if ((!profile.bodyMeasurements) || profile.bodyMeasurements == null) {
            navigate('/profile/body-measurements');
            showAlert(StatusCode.warning, "You have to enter body measurements first!")
            return;
        }
        if (profile.userGoals) {
            setLocalUserGoals(profile.userGoals);

            if (profile.bodyMeasurements.weight == null || profile.bodyMeasurements.height == null)
                return;
            calculatedBodyMeasurements.bmi = profile.bodyMeasurements.weight / (profile.bodyMeasurements.height / 100 * profile.bodyMeasurements.height / 100);

            if (profile.birthDate == null)
                return;
            const ageDifMs = Date.now() - new Date(profile.birthDate).getTime();
            const ageDate = new Date(ageDifMs);
            const age = Math.abs(ageDate.getUTCFullYear() - 1970);
            calculatedBodyMeasurements.age = age;

            if (profile.bodyMeasurements.leanBodyMass)
                calculatedBodyMeasurements.bmr = 370 + (21.6 * profile.bodyMeasurements.leanBodyMass)
            else if (profile.sex == 'M')
                calculatedBodyMeasurements.bmr = 10 * profile.bodyMeasurements.weight + 6.25 * profile.bodyMeasurements.height - 5 * calculatedBodyMeasurements.age + 5
            else if (profile.sex == 'F')
                calculatedBodyMeasurements.bmr = 10 * profile.bodyMeasurements.weight + 6.25 * profile.bodyMeasurements.height - 5 * calculatedBodyMeasurements.age - 161

            if (profile.bodyMeasurements.activityLevel == null)
                return;

            if (profile.bodyMeasurements.activityLevel == 0)
                calculatedBodyMeasurements.tdee = calculatedBodyMeasurements.bmr * 1.2
            if (profile.bodyMeasurements.activityLevel == 1)
                calculatedBodyMeasurements.tdee = calculatedBodyMeasurements.bmr * 1.375
            if (profile.bodyMeasurements.activityLevel == 2)
                calculatedBodyMeasurements.tdee = calculatedBodyMeasurements.bmr * 1.55
            if (profile.bodyMeasurements.activityLevel == 3)
                calculatedBodyMeasurements.tdee = calculatedBodyMeasurements.bmr * 1.725

            setCalculatedBodyMeasurements(calculatedBodyMeasurements);
        }
    }, [profile])
    const [errors, setErrors] = useState<Record<string, string>>({});
    const navigate = useNavigate();

    const handleInputChange = (e: any) => {
        const { id, value } = e.target;
        setLocalUserGoals((prevData) => ({
            ...prevData,
            [id]: value, // Update the corresponding field in state
        }));

        // validationSchema
        //     .validateAt(id, { ...localUserGoals, [id]: value })
        //     .then(() => setErrors((prev) => ({ ...prev, [id]: '' })))
        //     .catch((err) => setErrors((prev) => ({ ...prev, [id]: err.message })));
    };

    const handleCalculationEntityChange = (e: any) => {
        let { id, value } = e.target;
        if (id == 'percentageStep') value = parseInt(value);
        setCalculationEntity((prevData: any) => ({
            ...prevData,
            [id]: value
        }));
    }

    const getDistributionTypeDescription = () => {
        var selectedGoalType = goalTypeOptions.find(option => option.goalType == calculationEntity.goalType);
        if (selectedGoalType == null) return '';
        var selectedDistributionType = distributionTypeOptions.find(option => option.distributionType == calculationEntity.distributionType);
        if (selectedDistributionType == null) return '';

        var description = selectedDistributionType.description;
        description = description.replace('{gramsOfProteinPerKg}', selectedGoalType.gramsOfProteinPerKg.toString());
        description = description.replace('{gramsOfFatPerKg}', selectedGoalType.gramsOfFatPerKg.toString());

        //TODO: DUMI add correction case
        // if(selectedGoalType.descriptionAppend)
        //     description += ' ' + selectedGoalType.descriptionAppend;

        return description;
    }

    useEffect(() => {
        if (showCalculate) {
            var calculatedCalorieTarget = calculatedBodyMeasurements.tdee;
            if (calculationEntity.goalType == 0) {
                calculatedCalorieTarget = calculatedBodyMeasurements.tdee * ((100 - calculationEntity.percentageStep) / 100.0);
            } else if (calculationEntity.goalType == 2) {
                calculatedCalorieTarget = calculatedBodyMeasurements.tdee * ((100 + calculationEntity.percentageStep) / 100.0);
            }
            calculatedCalorieTarget = Math.round(calculatedCalorieTarget);

            var selectedDistributionType = distributionTypeOptions.find(option => option.distributionType == calculationEntity.distributionType);
            if (selectedDistributionType == null) return;
            var calculatedProteinTarget = selectedDistributionType.dailyProteinTarget;
            var calculatedCarbohydratesTarget = selectedDistributionType.dailyCarbohydratesTarget;
            var calculatedTotalFatTarget = selectedDistributionType.dailyTotalFatTarget;

            if (calculationEntity.distributionType == 0) {
                var selectedGoalType = goalTypeOptions.find(option => option.goalType == calculationEntity.goalType);
                if (selectedGoalType == null) return;
                calculatedProteinTarget = selectedGoalType.gramsOfProteinPerKg;
                calculatedTotalFatTarget = selectedGoalType.gramsOfFatPerKg;

                calculatedProteinTarget = Math.round((calculatedProteinTarget * profile.bodyMeasurements!.weight) * 400 / calculatedCalorieTarget);
                calculatedTotalFatTarget = Math.round((calculatedTotalFatTarget * profile.bodyMeasurements!.weight) * 900 / calculatedCalorieTarget);

                if ((profile.sex == 'M' && calculatedTotalFatTarget > 20) || (profile.sex == 'F' && calculatedTotalFatTarget > 30)) {
                    //TODO: DUMI add correction case
                    //If the body fat percentage is over 20% for men and 30% for women, the calculated protein intake is 40% of the total calorie count. Recommended if you exercise at least three times a week.
                }

                calculatedCarbohydratesTarget = 100 - calculatedProteinTarget - calculatedTotalFatTarget;
            }

            setLocalUserGoals((prevData: UserGoals) => ({
                ...prevData,
                dailyCalorieTarget: calculatedCalorieTarget,
                dailyProteinTarget: calculatedProteinTarget,
                dailyCarbohydratesTarget: calculatedCarbohydratesTarget,
                dailyTotalFatTarget: calculatedTotalFatTarget
            }));
        }
    }, [calculationEntity]);

    const handleSubmit = () => {
        validationSchema
            .validate(localUserGoals, { abortEarly: false })
            .then(() => {
                insertUserGoals(localUserGoals);
            })
            .catch((err) => {
                const validationErrors: Record<string, string> = {};
                err.inner.forEach((error: Yup.ValidationError) => {
                    if (error.path) validationErrors[error.path] = error.message;
                });
                setErrors(validationErrors);
            });
    };

    const handleCancelButton = () => {
        if (showCalculate)
            setShowCalculate(false);
        else
            navigate(-1);
    }
    return (
        <Form>
            {
                showCalculate &&
                <>
                    <FormSelect
                        entity={calculationEntity}
                        fieldName='goalType'
                        handleInputChange={handleCalculationEntityChange}
                        options={goalTypeOptions}
                        placeholder='Select an option'
                        label='Goal Type'
                    />
                    <FormSelect
                        entity={calculationEntity}
                        fieldName='distributionType'
                        handleInputChange={handleCalculationEntityChange}
                        options={distributionTypeOptions}
                        placeholder='Select an option'
                        label='Distribution Type'
                        mb={1}
                    />
                    {
                        calculationEntity.distributionType &&
                        <p className="text-muted mx-3">
                            {getDistributionTypeDescription()}
                        </p>
                    }
                    {
                        calculationEntity.goalType != null &&
                        <div>
                            {calculationEntity.goalType != 1 &&
                                <FormRange
                                    entity={calculationEntity}
                                    handleChange={handleCalculationEntityChange}
                                    fieldName='percentageStep'
                                    label={`${goalTypeOptions.filter(option => option.goalType == calculationEntity.goalType)[0].name} ${calculationEntity.percentageStep}% TDEE`}
                                    min={10}
                                    max={30}
                                    step={5}
                                />
                            }
                        </div>
                    }
                </>
            }
            {(!showCalculate || (calculationEntity.goalType && calculationEntity.distributionType)) &&
                <div>
                    <FormInput
                        entity={localUserGoals}
                        handleInputChange={handleInputChange}
                        fieldName='dailyCalorieTarget'
                        label='Daily Calorie Target'
                        disabled={showCalculate}
                        errors={errors}
                    />
                    <div className="d-flex flex-row justify-content-between gap-2">
                        <FormInput
                            entity={localUserGoals}
                            handleInputChange={handleInputChange}
                            fieldName='dailyProteinTarget'
                            label='Protein'
                            append='%'
                            disabled={showCalculate}
                            errors={errors}
                        />
                        <FormInput
                            entity={localUserGoals}
                            handleInputChange={handleInputChange}
                            fieldName='dailyCarbohydratesTarget'
                            label='Carbs'
                            append='%'
                            disabled={showCalculate}
                            errors={errors}
                        />
                        <FormInput
                            entity={localUserGoals}
                            handleInputChange={handleInputChange}
                            fieldName='dailyTotalFatTarget'
                            label='Fat'
                            append='%'
                            disabled={showCalculate}
                            errors={errors}
                        />
                    </div>
                    {!showCalculate &&
                        <FormButton
                            text={'Calculate new goals'}
                            onClick={() => setShowCalculate(true)}
                            color='success'
                            mb={3}
                            className='w-100'
                        />
                    }
                </div>
            }
            <FormActions
                mainAction={handleSubmit}
                mainActionText={"Save"}
                secondaryAction={handleCancelButton}
                secondaryActionText={"Cancel"}
            />
        </Form>
    );
};

export default GoalsBody;